<template>
  <div ref="dropdownBody" class="relative" @keyup.esc="closeDropdown">
    <div class="absolute z-20 mt-3 w-full rounded-lg bg-white shadow-xs shadow-gray-300">
      <ul class="flex flex-col gap-1.5 py-3 text-xs text-neutral-700">
        <li v-for="item in modelValue" :key="item.name" ref="itemRefs" class="relative list-none">
          <div
            ref="forceFocus"
            class="block cursor-pointer px-4 py-2 font-semibold hover:text-primary-500"
            :class="{ 'bg-primary-50 text-primary-500': item.active }"
            @click="clickedItem(item)"
          >
            <font-awesome-icon v-if="item.icon" :icon="item.icon" class="mr-2" />
            {{ item.label }}
          </div>
        </li>
      </ul>
    </div>

    <Teleport to="body">
      <div
        v-if="activeItem && isPositioned"
        ref="teleportedContent"
        class="fixed z-50"
        :style="teleportedContentStyle"
      >
        <slot :name="activeItem.name" />
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { UiDropdownItem } from '@infuse/ui/domain';
import { onClickOutside } from '@vueuse/core';
import { ref, watch, computed, nextTick } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

interface Props {
  modelValue: UiDropdownItem[];
  ignoreCloseOnClick?: HTMLElement[];
}

const props = withDefaults(defineProps<Props>(), {
  ignoreCloseOnClick: () => []
});

interface Emits {
  (e: 'update:modelValue', items: UiDropdownItem[]): void;

  (e: 'close'): void;
}

const emit = defineEmits<Emits>();

const dropdownBody = ref<HTMLElement>();
const teleportedContent = ref<HTMLElement>();
const forceFocus = ref();
const currentActive = ref<UiDropdownItem>();
const itemRefs = ref<HTMLElement[]>([]);
const teleportedContentStyle = ref<{ top: string; left: string }>({ top: '0px', left: '0px' });
const isPositioned = ref(false);

const activeItem = computed(() => props.modelValue.find((item) => item.active));

onClickOutside(dropdownBody, closeDropdown, { ignore: [...props.ignoreCloseOnClick, teleportedContent] });

watch(forceFocus, (newValue) => {
  if (newValue[0]) {
    newValue[0].focus();
  }
});

function clickedItem(item: UiDropdownItem): void {
  if (currentActive.value) {
    currentActive.value.active = false;
  }

  // Reset positioning flag before activating new item
  isPositioned.value = false;

  item.active = true;
  currentActive.value = item;
  emit('update:modelValue', props.modelValue);

  // Position the teleported content next to the clicked item
  nextTick(() => {
    const itemIndex = props.modelValue.findIndex((i) => i.name === item.name);
    const itemEl = itemRefs.value[itemIndex];
    if (itemEl) {
      const rect = itemEl.getBoundingClientRect();
      // Position to the right of the item on medium+ screens, below on mobile
      const isMediumScreen = window.innerWidth >= 768;
      if (isMediumScreen) {
        teleportedContentStyle.value = {
          top: `${rect.top}px`,
          left: `${rect.right}px`
        };
      } else {
        teleportedContentStyle.value = {
          top: `${rect.bottom}px`,
          left: `${rect.left}px`
        };
      }
      // Only show after position is calculated
      isPositioned.value = true;
    }
  });
}

function closeDropdown(): void {
  emit('close');
}
</script>
